import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import {
  buildSettingsSavePayload,
  getSettingsErrorMessage,
  saveSettings,
  validateSettingsForm,
} from "../../services/whatsappSetupApi";

const EMPTY_FORM = {
  access_token: "",
  phone_number_id: "",
  waba_id: "",
  app_id: "",
  app_secret: "",
  webhook_verify_token: "",
};

function secretPlaceholder(hasValue) {
  return hasValue ? "Already saved — leave blank to keep" : "";
}

export default function WhatsAppSetupModal({
  show,
  onHide,
  onSuccess,
  initialSettings = null,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const isFirstSetup = !initialSettings?.configured;

  useEffect(() => {
    if (!show) return;
    setFieldErrors({});
    setSubmitError("");
    setForm({
      access_token: "",
      phone_number_id: initialSettings?.phoneNumberId || "",
      waba_id: initialSettings?.wabaId || "",
      app_id: initialSettings?.appId || "",
      app_secret: "",
      webhook_verify_token: "",
    });
  }, [show, initialSettings]);

  const helpBlocks = useMemo(
    () => ({
      access_token:
        "From Meta Developer → WhatsApp → API Setup (temporary or permanent token).",
      phone_number_id:
        "From Meta Business / WhatsApp Manager → Phone numbers.",
      waba_id: "WhatsApp Business Account ID from Meta Business Manager.",
      app_secret:
        "Meta App Secret — recommended for webhook signature validation.",
      webhook_verify_token:
        "Custom verify token for Meta webhook subscription.",
    }),
    [],
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateSettingsForm(form, initialSettings);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    setSubmitError("");
    try {
      const payload = buildSettingsSavePayload(form, initialSettings);
      const saved = await saveSettings(payload);
      onSuccess?.(saved);
    } catch (err) {
      setSubmitError(getSettingsErrorMessage(err, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>WhatsApp Business Setup</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Connect your Meta WhatsApp Business credentials for this tenant.
            Secrets are stored securely on the server and never returned to the
            browser.
          </p>

          {submitError ? (
            <div className="alert alert-danger py-2 small" role="alert">
              {submitError}
            </div>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>
              Meta Access Token {isFirstSetup ? "*" : ""}
            </Form.Label>
            <Form.Control
              type="password"
              autoComplete="off"
              value={form.access_token}
              placeholder={secretPlaceholder(initialSettings?.hasAccessToken)}
              onChange={(e) => updateField("access_token", e.target.value)}
              isInvalid={Boolean(fieldErrors.access_token)}
              disabled={saving}
            />
            <Form.Text className="text-muted">{helpBlocks.access_token}</Form.Text>
            <Form.Control.Feedback type="invalid">
              {fieldErrors.access_token}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  Phone Number ID {isFirstSetup ? "*" : ""}
                </Form.Label>
                <Form.Control
                  type="text"
                  value={form.phone_number_id}
                  onChange={(e) =>
                    updateField("phone_number_id", e.target.value)
                  }
                  isInvalid={Boolean(fieldErrors.phone_number_id)}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  {helpBlocks.phone_number_id}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.phone_number_id}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>WABA ID (Business Account) {isFirstSetup ? "*" : ""}</Form.Label>
                <Form.Control
                  type="text"
                  value={form.waba_id}
                  onChange={(e) => updateField("waba_id", e.target.value)}
                  isInvalid={Boolean(fieldErrors.waba_id)}
                  disabled={saving}
                />
                <Form.Text className="text-muted">{helpBlocks.waba_id}</Form.Text>
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.waba_id}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Meta App ID</Form.Label>
            <Form.Control
              type="text"
              value={form.app_id}
              onChange={(e) => updateField("app_id", e.target.value)}
              disabled={saving}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Meta App Secret</Form.Label>
            <Form.Control
              type="password"
              autoComplete="off"
              value={form.app_secret}
              placeholder={secretPlaceholder(initialSettings?.hasAppSecret)}
              onChange={(e) => updateField("app_secret", e.target.value)}
              disabled={saving}
            />
            <Form.Text className="text-muted">{helpBlocks.app_secret}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Webhook Verify Token</Form.Label>
            <Form.Control
              type="password"
              autoComplete="off"
              value={form.webhook_verify_token}
              placeholder={secretPlaceholder(
                initialSettings?.hasWebhookVerifyToken,
              )}
              onChange={(e) =>
                updateField("webhook_verify_token", e.target.value)
              }
              disabled={saving}
            />
            <Form.Text className="text-muted">
              {helpBlocks.webhook_verify_token}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  role="status"
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
